import { Component, Type } from '@angular/core';
import { TNil } from '@app/app.interface';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { ITextFinding } from '@interfaces/analytics-details-utlilities.interface';
import { IBaseDeliverableAnalyticsApi, IExpectedKpiApi } from '@interfaces/campaign-analytics-api.interface';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TranslateService } from '@ngx-translate/core';
import { EAnalyticsStatus } from '@shared/enums/campaign-analytics.enum';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { differenceInMinutes } from 'date-fns';
import { camelCase } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { ICtaAnalyticsData } from '../../cta-analytics-table.interface';
import { IDeliverableCard } from '../cta-analytics-details-deliverable-card/cta-analytics-details-deliverable-card.interface';
import { IKpiCard } from '../cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import {
  DELIVERABLE_COMPONENT_MAP,
  DELIVERABLE_PROCESSING_TIME_MAP,
  ICalculationConfiguration,
  IFormattedKeywordAnalytics,
  TSharedCardData,
} from './base-analytics-formatter.interface';

export abstract class BaseAnalyticsFormatter {
  protected readonly dateUtils!: DateUtilitiesService;
  protected readonly durationUtilitiesService!: DurationUtilitiesService;

  protected constructor(
    protected readonly platformFeature: EPlatformFeature,
    protected readonly execution: ICtaAnalyticsData,
    protected readonly translate: TranslateService,
  ) {
    this.dateUtils = new DateUtilitiesService();
    this.durationUtilitiesService = new DurationUtilitiesService(translate);
  }

  public generateKpiCardData(config?: ICalculationConfiguration): IKpiCard[] {
    const socialContent = this.execution.content?.socialContent;

    if (socialContent) {
      return [
        this.getCommonKpiCardData('averageViewers', socialContent.kpiAnalytics.avgViewers, config),
        this.getCommonKpiCardData('peakViewers', socialContent.kpiAnalytics.peakViewers, config),
        this.getCommonKpiCardData('totalWatchedHours', socialContent.kpiAnalytics.totalWatchedHours, config),
        this.getCommonKpiCardData('likes', socialContent.kpiAnalytics.likes, config),
        this.getCommonKpiCardData('comments', socialContent.kpiAnalytics.comments, config),
        this.getCommonKpiCardData('engagementRate', socialContent.kpiAnalytics.engagementRate, config),
        this.getCommonKpiCardData('engagementRateViews', socialContent.kpiAnalytics.engagementRateViews, config),
        this.getCommonKpiCardData('engagements', socialContent.kpiAnalytics.engagements, config),
        this.getCommonKpiCardData('impressions', socialContent.kpiAnalytics.impressions, config),
        this.getCommonKpiCardData('predictedImpressions', socialContent.kpiAnalytics.predictedImpressions, config),
        this.getCommonKpiCardData('shares', socialContent.kpiAnalytics.shares, config),
        this.getCommonKpiCardData('quotes', socialContent.kpiAnalytics.quotes, config),
        this.getCommonKpiCardData('retweets', socialContent.kpiAnalytics.retweets, config),
        this.getCommonKpiCardData('views', socialContent.kpiAnalytics.views, config),
        this.getCommonKpiCardData('reach', socialContent.kpiAnalytics.reach, config),
        // Hidden for now
        // {
        //   ...this.getCommonKpiCardData('mediaValue', null, this.execution.ctaAnalytics?.mediaValue?.mediaValue),
        //   valueSuffix: this.execution.ctaAnalytics?.mediaValue?.currency ?? 'USD',
        // },
      ].filter((kpi) => config?.includeKpisWithNullValue || kpi.value !== null);
    }

    if (this.execution.expectedKpis?.length) {
      return this.execution.expectedKpis.map((kpi) => this.getCommonKpiCardData(kpi.kpi as TExpectedKpi, null, config));
    }

    return [];
  }

  public abstract generateDeliverableCardData(): IDeliverableCard[];

  /**
   * Maps every `EDeliverableType` to its card-data processor.
   * `Record<EDeliverableType, …>` requires all keys — adding a new enum value
   * without a corresponding entry here is a compile error.
   */
  protected readonly deliverableProcessors: Record<EDeliverableType, () => IDeliverableCard[] | void> = {
    [EDeliverableType.audioTracking]: () => this.processAudioTracking(),
    [EDeliverableType.caption]: () => this.processCaption(),
    [EDeliverableType.chatBot]: () => this.processChatbot(),
    [EDeliverableType.chatbotMulti]: () => this.processChatbotMulti(),
    [EDeliverableType.content]: () => this.processContent(),
    [EDeliverableType.custom]: () => this.processCustom(),
    [EDeliverableType.description]: () => this.processDescription(),
    [EDeliverableType.gamePlay]: () => this.processGamePlay(),
    [EDeliverableType.hashTag]: () => this.processHashTag(),
    [EDeliverableType.mention]: () => this.processMention(),
    [EDeliverableType.message]: () => this.processMessage(),
    [EDeliverableType.productPlacement]: () => this.processProductPlacements(),
    [EDeliverableType.showcase]: () => this.processShowcase(),
    [EDeliverableType.talkingPoint]: () => this.processTalkingPoints(),
    [EDeliverableType.talkingPointAi]: () => this.processTalkingPointAis(),
    [EDeliverableType.textTracking]: () => this.processTextTracking(),
    [EDeliverableType.title]: () => this.processTitle(),
    [EDeliverableType.visual]: () => this.processVisual(),
  };

  protected processTextTracking(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.textTrackings?.length) {
      return;
    }

    const { textTrackings: analyticstextTrackings } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];

    const sharedData = this.getSharedCardData(
      EDeliverableType.textTracking,
      this.translate.instant('pages.campaignDetails.ctaAnalytics.deliverable.textTracking.shortInfo'),
    );

    this.execution.ctaAnalytics.textTrackings.forEach((textTracking) => {
      const analyticstTextTracking = analyticstextTrackings?.find(
        (t) => t.deliverableId === textTracking.deliverableId,
      );
      const baseData = this.getBaseCardData(
        textTracking,
        sharedData,
        this.translate.instant('shared.analyticsStatus.processing'),
      );

      if (analyticstTextTracking) {
        const {
          names: keywordNames,
          texts,
          totalEntriesCount,
        } = this.extractKeywordAnalyticsData(
          analyticstTextTracking,
          (obj) => obj.keywords ?? [],
          (keyword) => keyword.keyword,
          (keyword) => keyword.findings ?? [],
        );

        const counts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywordNames, texts);
        const totalKeywordCount = Object.values(counts).reduce((sum, val) => sum + val, 0);

        deliverableCards.push({
          ...baseData,
          lastChanged: analyticstTextTracking.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticstTextTracking.lastChanged)
            : null,
          lastProcessed: analyticstTextTracking.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticstTextTracking.lastProcessed)
            : null,
          shortInfoValue: String(totalEntriesCount),
          sponsoredContentRelevant: analyticstTextTracking.sponsoredContentRelevant,
          status: analyticstTextTracking.status,
          statusManual: analyticstTextTracking.statusManual,
          value: `${totalKeywordCount}${analyticstTextTracking.keywords?.length ? `/${analyticstTextTracking.keywords?.length}` : ''}`,
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processAudioTracking(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.audioTrackings?.length) {
      return;
    }

    const { audioTrackings: analyticsAudioTrackings } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];

    const sharedData = this.getSharedCardData(
      EDeliverableType.audioTracking,
      this.translate.instant('pages.campaignDetails.ctaAnalytics.deliverable.audioTracking.shortInfo'),
    );

    this.execution.ctaAnalytics.audioTrackings.forEach((audioTracking) => {
      const analyticstAudioTracking = analyticsAudioTrackings?.find(
        (t) => t.deliverableId === audioTracking.deliverableId,
      );
      const baseData = this.getBaseCardData(
        audioTracking,
        sharedData,
        this.translate.instant('shared.analyticsStatus.processing'),
      );

      if (analyticstAudioTracking) {
        const {
          names: keywordNames,
          texts,
          totalEntriesCount,
        } = this.extractKeywordAnalyticsData(
          analyticstAudioTracking,
          (obj) => obj.keywords ?? [],
          (keyword) => keyword.keyword,
          (keyword) => keyword.findings ?? [],
        );

        const counts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywordNames, texts);
        const totalKeywordCount = Object.values(counts).reduce((sum, val) => sum + val, 0);

        deliverableCards.push({
          ...baseData,
          lastChanged: analyticstAudioTracking.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticstAudioTracking.lastChanged)
            : null,
          lastProcessed: analyticstAudioTracking.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticstAudioTracking.lastProcessed)
            : null,
          shortInfoValue: String(totalEntriesCount),
          status: analyticstAudioTracking.status,
          statusManual: analyticstAudioTracking.statusManual,
          value: `${totalKeywordCount}${analyticstAudioTracking.keywords?.length ? `/${analyticstAudioTracking.keywords?.length}` : ''}`,
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processCaption(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.captions?.length) {
      return;
    }

    const { captions: analyticsCaptions } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];

    const sharedData = this.getSharedCardData(
      EDeliverableType.caption,
      this.translate.instant('pages.campaignDetails.ctaAnalytics.deliverable.caption.shortInfo'),
    );

    this.execution.ctaAnalytics.captions.forEach((caption) => {
      const analyticsCaption = analyticsCaptions?.find((c) => c.deliverableId === caption.deliverableId);
      const baseData = this.getBaseCardData(caption, sharedData);

      if (analyticsCaption) {
        const okKeywords = analyticsCaption.keywords?.filter((k) => k.status === EAnalyticsStatus.ok) ?? [];

        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsCaption.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsCaption.lastChanged)
            : null,
          lastProcessed: analyticsCaption.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsCaption.lastProcessed)
            : null,
          shortInfoValue: String(okKeywords.length),
          status: analyticsCaption.status,
          statusManual: analyticsCaption.statusManual,
          value: `${okKeywords.length}/${analyticsCaption.keywords?.length}`,
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processHashTag(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.hashtags?.length) {
      return;
    }

    const { hashtags: analyticsHashtags } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];

    const sharedData = this.getSharedCardData(
      EDeliverableType.hashTag,
      this.translate.instant('pages.campaignDetails.ctaAnalytics.deliverable.hashTag.shortInfo'),
    );

    this.execution.ctaAnalytics.hashtags.forEach((hashtag) => {
      const analyticsHashtag = analyticsHashtags?.find((h) => h.deliverableId === hashtag.deliverableId);
      const baseData = this.getBaseCardData(hashtag, sharedData);

      if (analyticsHashtag) {
        const okKeywords = analyticsHashtag.keywords?.filter((k) => k.status === EAnalyticsStatus.ok) ?? [];

        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsHashtag.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsHashtag.lastChanged)
            : null,
          lastProcessed: analyticsHashtag.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsHashtag.lastProcessed)
            : null,
          shortInfoValue: String(okKeywords.length),
          status: analyticsHashtag.status,
          statusManual: analyticsHashtag.statusManual,
          value: `${okKeywords.length}/${analyticsHashtag.keywords?.length}`,
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processMention(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.mentions?.length) {
      return;
    }

    const { mentions: analyticsMentions } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];

    const sharedData = this.getSharedCardData(
      EDeliverableType.mention,
      this.translate.instant('pages.campaignDetails.ctaAnalytics.deliverable.mention.shortInfo'),
    );

    this.execution.ctaAnalytics.mentions.forEach((mention) => {
      const analyticsMention = analyticsMentions?.find((m) => m.deliverableId === mention.deliverableId);
      const baseData = this.getBaseCardData(mention, sharedData);

      if (analyticsMention) {
        const okKeywords = analyticsMention.keywords?.filter((k) => k.status === EAnalyticsStatus.ok) ?? [];

        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsMention.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsMention.lastChanged)
            : null,
          lastProcessed: analyticsMention.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsMention.lastProcessed)
            : null,
          shortInfoValue: String(okKeywords.length),
          status: analyticsMention.status,
          statusManual: analyticsMention.statusManual,
          value: `${okKeywords.length}/${analyticsMention.keywords?.length}`,
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processTalkingPoints(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.talkingPoints?.length) {
      return;
    }

    const { talkingPoints: analyticsTalkingPoints } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];
    const sharedData = this.getSharedCardData(
      EDeliverableType.talkingPoint,
      this.translate.instant('pages.campaignDetails.ctaAnalytics.deliverable.talkingPoint.shortInfo'),
    );

    this.execution.ctaAnalytics.talkingPoints.forEach((talkingPoint) => {
      const analyticsTalkingPoint = analyticsTalkingPoints?.find(
        (tp) => tp.deliverableId === talkingPoint.deliverableId,
      );
      const baseData = this.getBaseCardData(talkingPoint, sharedData);

      let totalKeywordCount = 0;

      if (analyticsTalkingPoint) {
        const {
          names: topicNames,
          texts,
          totalEntriesCount,
        } = this.extractKeywordAnalyticsData(
          analyticsTalkingPoint,
          (obj) => obj.keywords ?? [],
          (topic) => topic.keyword,
          (topic) => topic.findings ?? [],
        );

        const counts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(topicNames, texts);
        totalKeywordCount = Object.values(counts).reduce((sum, val) => sum + val, 0);

        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsTalkingPoint.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsTalkingPoint.lastChanged)
            : null,
          lastProcessed: analyticsTalkingPoint.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsTalkingPoint.lastProcessed)
            : null,
          shortInfoValue: String(totalEntriesCount),
          status: analyticsTalkingPoint.status,
          statusManual: analyticsTalkingPoint.statusManual,
          value: `${totalKeywordCount}${analyticsTalkingPoint.keywords?.length ? `/${analyticsTalkingPoint.keywords?.length}` : ''}`,
        });

        return;
      }

      deliverableCards.push({
        ...baseData,
        value: `${totalKeywordCount}${talkingPoint.keywords?.length ? `/${talkingPoint.keywords?.length}` : ''}`,
      });
    });

    return deliverableCards;
  }

  protected processTalkingPointAis(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.talkingPointAis?.length) {
      return;
    }

    const { talkingPointAis: analyticsTalkingPointAis } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];
    const sharedData = this.getSharedCardData(
      EDeliverableType.talkingPointAi,
      this.translate.instant('pages.campaignDetails.ctaAnalytics.deliverable.talkingPointAi.shortInfo'),
    );

    this.execution.ctaAnalytics.talkingPointAis.forEach((talkingPointAi) => {
      const analyticsTalkingPointAi = analyticsTalkingPointAis?.find(
        (tp) => tp.deliverableId === talkingPointAi.deliverableId,
      );
      const baseData = this.getBaseCardData(talkingPointAi, sharedData);

      if (analyticsTalkingPointAi) {
        const findingsCount = analyticsTalkingPointAi.findingsCount ?? analyticsTalkingPointAi.findings?.length ?? 0;

        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsTalkingPointAi.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsTalkingPointAi.lastChanged)
            : null,
          lastProcessed: analyticsTalkingPointAi.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsTalkingPointAi.lastProcessed)
            : null,
          shortInfoValue: String(findingsCount),
          status: analyticsTalkingPointAi.status,
          statusManual: analyticsTalkingPointAi.statusManual,
          value: String(findingsCount),
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processVisual(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.visuals?.length) {
      return;
    }

    const { visuals: analyticsVisuals } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];
    const sharedData = this.getSharedCardData(EDeliverableType.visual);

    this.execution.ctaAnalytics.visuals.forEach((visual) => {
      const analyticsVisual = analyticsVisuals?.find((v) => v.deliverableId === visual.deliverableId);
      const baseData = this.getBaseCardData(
        visual,
        sharedData,
        this.translate.instant('shared.analyticsStatus.processing'),
      );

      if (analyticsVisual) {
        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsVisual.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsVisual.lastChanged)
            : null,
          lastProcessed: analyticsVisual.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsVisual.lastProcessed)
            : null,
          status: analyticsVisual.status,
          statusManual: analyticsVisual.statusManual,
          value: this.translate.instant(
            `shared.analyticsStatus.${camelCase(analyticsVisual?.statusManual ?? analyticsVisual.status)}`,
          ),
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processMessage(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.messages?.length) {
      return;
    }

    const { messages: analyticsMessages } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];

    const sharedData = this.getSharedCardData(
      EDeliverableType.message,
      this.translate.instant('pages.campaignDetails.ctaAnalytics.deliverable.message.shortInfo'),
    );

    this.execution.ctaAnalytics.messages.forEach((message) => {
      const analyticsMessage = analyticsMessages?.find((m) => m.deliverableId === message.deliverableId);
      const baseData = this.getBaseCardData(message, sharedData);

      if (analyticsMessage) {
        const okKeywords = analyticsMessage.keywords?.filter((k) => k.status === EAnalyticsStatus.ok) ?? [];

        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsMessage.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsMessage.lastChanged)
            : null,
          lastProcessed: analyticsMessage.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsMessage.lastProcessed)
            : null,
          shortInfoValue: String(okKeywords.length),
          status: analyticsMessage.status,
          statusManual: analyticsMessage.statusManual,
          value: `${okKeywords.length}/${analyticsMessage.keywords?.length}`,
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processDescription(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.descriptions?.length) {
      return;
    }

    const { descriptions: analyticsDescriptions } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];

    const sharedData = this.getSharedCardData(
      EDeliverableType.description,
      this.translate.instant('pages.campaignDetails.ctaAnalytics.deliverable.description.shortInfo'),
    );

    this.execution.ctaAnalytics.descriptions.forEach((description) => {
      const analyticsDescription = analyticsDescriptions?.find((d) => d.deliverableId === description.deliverableId);
      const baseData = this.getBaseCardData(description, sharedData);

      if (analyticsDescription) {
        const okKeywords = analyticsDescription.keywords?.filter((k) => k.status === EAnalyticsStatus.ok) ?? [];

        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsDescription.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsDescription.lastChanged)
            : null,
          lastProcessed: analyticsDescription.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsDescription.lastProcessed)
            : null,
          shortInfoValue: String(okKeywords.length),
          status: analyticsDescription.status,
          statusManual: analyticsDescription.statusManual,
          value: `${okKeywords.length}/${analyticsDescription.keywords?.length}`,
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processTitle(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.titles?.length) {
      return;
    }

    const { titles: analyticsTitles } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];

    const sharedData = this.getSharedCardData(
      EDeliverableType.title,
      this.translate.instant('pages.campaignDetails.ctaAnalytics.deliverable.title.shortInfo'),
    );

    this.execution.ctaAnalytics.titles.forEach((title) => {
      const analyticsTitle = analyticsTitles?.find((t) => t.deliverableId === title.deliverableId);
      const baseData = this.getBaseCardData(
        title,
        sharedData,
        this.translate.instant('shared.analyticsStatus.processing'),
      );

      if (analyticsTitle) {
        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsTitle.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsTitle.lastChanged)
            : null,
          lastProcessed: analyticsTitle.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsTitle.lastProcessed)
            : null,
          shortInfoValue: String(analyticsTitle.keywords?.length),
          status: analyticsTitle.status,
          statusManual: analyticsTitle.statusManual,
          value: this.translate.instant(
            `shared.analyticsStatus.${camelCase(analyticsTitle?.statusManual ?? analyticsTitle.status)}`,
          ),
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processChatbot(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.chatbot?.length) {
      return;
    }

    const { chatbot: analyticsChatbots } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];
    const duration = this.execution.duration ?? null;
    const sharedData = this.getSharedCardData(EDeliverableType.chatBot);

    this.execution.ctaAnalytics.chatbot.forEach((chatbot) => {
      const analyticsChatbot = analyticsChatbots?.find((cb) => cb.deliverableId === chatbot.deliverableId);
      const baseData = this.getBaseCardData(chatbot, sharedData);
      const chatbotFindingsCount = analyticsChatbot?.findings?.length ?? 0;
      const totalChatbotCount = Math.floor(duration ? duration / Number(chatbot.interval) : 0);

      if (analyticsChatbot) {
        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsChatbot.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsChatbot.lastChanged)
            : null,
          lastProcessed: analyticsChatbot.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsChatbot.lastProcessed)
            : null,
          status: analyticsChatbot.status,
          statusManual: analyticsChatbot.statusManual,
          value: `${chatbotFindingsCount}${duration ? `/${totalChatbotCount}` : ''}`,
        });

        return;
      }

      deliverableCards.push({
        ...baseData,
        value: `${chatbotFindingsCount}${duration ? `/${totalChatbotCount}` : ''}`,
      });
    });

    return deliverableCards;
  }

  protected processChatbotMulti(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.chatbotMultis?.length) {
      return;
    }

    const { chatbotMultis: analyticsChatbotMultis } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];
    const sharedData = this.getSharedCardData(EDeliverableType.chatbotMulti);

    this.execution.ctaAnalytics.chatbotMultis.forEach((chatbotMulti) => {
      const analyticsChatbotMulti = analyticsChatbotMultis?.find(
        (cm) => cm.deliverableId === chatbotMulti.deliverableId,
      );
      const baseData = this.getBaseCardData(chatbotMulti, sharedData);
      const findingsCount = analyticsChatbotMulti?.findings?.length ?? 0;

      if (analyticsChatbotMulti) {
        const okKeywords = analyticsChatbotMulti.keywords?.filter((k) => k.status === EAnalyticsStatus.ok) ?? [];

        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsChatbotMulti.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsChatbotMulti.lastChanged)
            : null,
          lastProcessed: analyticsChatbotMulti.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsChatbotMulti.lastProcessed)
            : null,
          shortInfoValue: `${okKeywords.length}/${analyticsChatbotMulti.keywords?.length ?? 0}`,
          status: analyticsChatbotMulti.status,
          statusManual: analyticsChatbotMulti.statusManual,
          value: String(findingsCount),
        });

        return;
      }

      deliverableCards.push({
        ...baseData,
        value: String(findingsCount),
      });
    });

    return deliverableCards;
  }

  protected processGamePlay(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.gamePlay?.length) {
      return;
    }

    const { gamePlay: analyticsGamePlays } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];
    const sharedData = this.getSharedCardData(EDeliverableType.gamePlay);

    this.execution.ctaAnalytics.gamePlay.forEach((gamePlay) => {
      const analyticsGamePlay = analyticsGamePlays?.find((gp) => gp.deliverableId === gamePlay.deliverableId);
      const baseData = this.getBaseCardData(gamePlay, sharedData);

      if (analyticsGamePlay) {
        const totalDuration =
          analyticsGamePlay.findings?.reduce((acc: number, finding) => {
            if (finding && typeof finding.start === 'string' && typeof finding.end === 'string') {
              return acc + differenceInMinutes(new Date(finding.end), new Date(finding.start));
            }

            return acc;
          }, 0) ?? 0;

        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsGamePlay.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsGamePlay.lastChanged)
            : null,
          lastProcessed: analyticsGamePlay.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsGamePlay.lastProcessed)
            : null,
          status: analyticsGamePlay.status,
          statusManual: analyticsGamePlay.statusManual,
          value: this.durationUtilitiesService.getDuration(totalDuration),
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processProductPlacements(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.productPlacements?.length) {
      return;
    }

    const { productPlacements: analyticsProductPlacements } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];
    const sharedData = this.getSharedCardData(EDeliverableType.productPlacement);

    this.execution.ctaAnalytics.productPlacements.forEach((productPlacement) => {
      const analyticsProductPlacement = analyticsProductPlacements?.find(
        (pp) => pp.deliverableId === productPlacement.deliverableId,
      );
      const baseData = this.getBaseCardData(
        productPlacement,
        sharedData,
        this.translate.instant('shared.analyticsStatus.processing'),
      );

      if (analyticsProductPlacement) {
        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsProductPlacement.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsProductPlacement.lastChanged)
            : null,
          lastProcessed: analyticsProductPlacement.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsProductPlacement.lastProcessed)
            : null,
          status: analyticsProductPlacement.status,
          statusManual: analyticsProductPlacement.statusManual,
          value: this.translate.instant(
            `shared.analyticsStatus.${camelCase(analyticsProductPlacement?.statusManual ?? analyticsProductPlacement.status)}`,
          ),
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processShowcase(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.showcases?.length) {
      return;
    }

    const { showcases: analyticsShowcases } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];
    const sharedData = this.getSharedCardData(EDeliverableType.showcase);

    this.execution.ctaAnalytics.showcases.forEach((showcase) => {
      const analyticsShowcase = analyticsShowcases?.find((s) => s.deliverableId === showcase.deliverableId);
      const baseData = this.getBaseCardData(
        showcase,
        sharedData,
        this.translate.instant('shared.analyticsStatus.processing'),
      );

      if (analyticsShowcase) {
        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsShowcase.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsShowcase.lastChanged)
            : null,
          lastProcessed: analyticsShowcase.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsShowcase.lastProcessed)
            : null,
          status: analyticsShowcase.status,
          statusManual: analyticsShowcase.statusManual,
          value: this.translate.instant(
            `shared.analyticsStatus.${camelCase(analyticsShowcase?.statusManual ?? analyticsShowcase.status)}`,
          ),
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processCustom(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.customs?.length) {
      return;
    }

    const { customs: analyticsCustoms } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];
    const sharedData = this.getSharedCardData(EDeliverableType.custom);

    this.execution.ctaAnalytics.customs.forEach((custom) => {
      const analyticsCustom = analyticsCustoms?.find((s) => s.deliverableId === custom.deliverableId);
      const baseData = this.getBaseCardData(
        custom,
        sharedData,
        this.translate.instant('shared.analyticsStatus.processing'),
      );

      if (analyticsCustom) {
        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsCustom.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsCustom.lastChanged)
            : null,
          lastProcessed: analyticsCustom.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsCustom.lastProcessed)
            : null,
          status: analyticsCustom.status,
          statusManual: analyticsCustom.statusManual,
          value: this.translate.instant(
            `shared.analyticsStatus.${camelCase(analyticsCustom?.statusManual ?? analyticsCustom.status)}`,
          ),
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected processContent(): IDeliverableCard[] | void {
    if (!this.execution.ctaAnalytics?.contents?.length) {
      return;
    }

    const { contents: analyticsContents } = this.execution.ctaAnalytics ?? {};
    const deliverableCards: IDeliverableCard[] = [];

    const sharedData = this.getSharedCardData(
      EDeliverableType.content,
      this.translate.instant('pages.campaignDetails.ctaAnalytics.deliverable.content.shortInfo'),
    );

    this.execution.ctaAnalytics.contents.forEach((content) => {
      const analyticsContent = analyticsContents?.find((c) => c.deliverableId === content.deliverableId);
      const baseData = this.getBaseCardData(
        content,
        sharedData,
        this.translate.instant('shared.analyticsStatus.processing'),
      );

      if (analyticsContent) {
        deliverableCards.push({
          ...baseData,
          lastChanged: analyticsContent.lastChanged
            ? this.dateUtils.parseDateTimeFromApi(analyticsContent.lastChanged)
            : null,
          lastProcessed: analyticsContent.lastProcessed
            ? this.dateUtils.parseDateTimeFromApi(analyticsContent.lastProcessed)
            : null,
          status: analyticsContent.status,
          statusManual: analyticsContent.statusManual,
          value: this.translate.instant(
            `shared.analyticsStatus.${camelCase(analyticsContent?.statusManual ?? analyticsContent.status)}`,
          ),
        });

        return;
      }

      deliverableCards.push(baseData);
    });

    return deliverableCards;
  }

  protected extractKeywordAnalyticsData<T, Item, Entry>(
    analyticsObject: T,
    getCollection: (obj: T) => Item[],
    getItemName: (item: Item) => string,
    getEntries: (item: Item) => Entry[],
  ): IFormattedKeywordAnalytics {
    const names: string[] = [];
    const allEntries: ITextFinding[] = [];

    const collection = getCollection(analyticsObject) ?? [];

    for (const item of collection) {
      names.push(getItemName(item));
      allEntries.push(...((getEntries(item) ?? []) as ITextFinding[]));
    }

    const uniqueFindings = AnalyticsDetailsUtilitiesService.getUniqueTextFindings(allEntries) as string[];

    return { names, texts: uniqueFindings, totalEntriesCount: uniqueFindings.length };
  }

  protected getExpectedKpi(kpi: TExpectedKpi): IExpectedKpiApi | undefined {
    return this.execution.expectedKpis?.find((expectedKpi) => expectedKpi.kpi === kpi);
  }

  protected getCommonKpiCardData(
    kpi: TExpectedKpi,
    value: number | TNil,
    config?: ICalculationConfiguration,
  ): IKpiCard {
    const expectedKpi = this.getExpectedKpi(kpi);
    const isPercentage = kpi === 'engagementRate' || kpi === 'engagementRateViews';
    const kpiKey =
      kpi === 'averageViewers' ? 'avgViewers' : kpi === 'sponsoredAverageViewers' ? 'sponsoredAvgViewers' : kpi;

    return {
      icon: config?.icon ?? null,
      id: expectedKpi?.expectedKpiId ?? uuidv4(),
      isPercentage,
      kpiKey,
      showValueTooltip: true,
      statusKpi: this.execution.tileConfiguration.ctaKpiStatusable?.statusKpi ?? null,
      targetValue: config?.ignoreExpectedKpi ? null : (expectedKpi?.expectedValue ?? null),
      title: this.translate.instant(`pages.campaignDetails.ctaAnalytics.kpi.${kpi}`),
      value,
    };
  }

  protected getSharedCardData(type: EDeliverableType, shortInfo?: string): TSharedCardData {
    return {
      campaignId: this.execution.campaignId,
      campaignProcessStatus: this.execution.campaignProcessStatus,
      campaignStatus: this.execution.campaignStatus,
      component: DELIVERABLE_COMPONENT_MAP[type] as Type<Component>,
      creatorId: this.execution.creatorId,
      ctaTemplateId: this.execution.ctaTemplateId,
      lastChanged: null,
      lastProcessed: null,
      processingTime: this.translate.instant(DELIVERABLE_PROCESSING_TIME_MAP[type]),
      shortInfo,
      socialContentId: this.execution.socialContentId ?? '',
      title: this.translate.instant(`pages.campaignDetails.ctaAnalytics.deliverable.${type}.title`),
      titleTooltip: this.translate.instant(`pages.campaignDetails.ctaAnalytics.deliverable.${type}.tooltip`),
      type,
    };
  }

  protected getBaseCardData(
    deliverableAnalytics: IBaseDeliverableAnalyticsApi,
    sharedData: TSharedCardData,
    defaultValue?: string,
  ): IDeliverableCard {
    return {
      ...sharedData,
      id: deliverableAnalytics.deliverableId,
      lastChanged: null,
      lastProcessed: null,
      name: deliverableAnalytics.name,
      shortInfoValue: null,
      sponsoredContentRelevant: deliverableAnalytics?.sponsoredContentRelevant ?? true,
      status: EAnalyticsStatus.processing,
      uniqueId: uuidv4(),
      value: defaultValue ?? null,
    };
  }
}
